<template>
  <div class="container mx-auto px-4 py-8">
    <div v-if="loading" class="flex justify-center py-12">
      <div class="spinner w-8 h-8"></div>
    </div>

    <div v-else-if="qna" class="max-w-4xl mx-auto">
      <!-- 헤더 -->
      <div class="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div class="flex justify-between items-start mb-4">
          <div>
            <span class="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full mb-3">
              Q&A
            </span>
            <h1 class="text-2xl font-bold text-gray-900">{{ qna.title }}</h1>
          </div>
          
          <!-- 관리 버튼 (작성자 또는 관리자만) -->
          <div v-if="canManageQna" class="flex gap-2">
            <RouterLink
              :to="`/qna/${qna.id}/edit`"
              class="btn btn-outline-primary btn-sm"
            >
              <PencilIcon class="w-4 h-4 mr-2" />
              수정
            </RouterLink>
            <button
              @click="deleteQna"
              class="btn btn-outline-danger btn-sm"
            >
              <TrashIcon class="w-4 h-4 mr-2" />
              삭제
            </button>
          </div>
        </div>

        <!-- 메타 정보 -->
        <div class="flex items-center text-sm text-gray-600 space-x-4 mb-6">
          <div class="flex items-center">
            <UserIcon class="w-4 h-4 mr-1" />
            {{ qna.author.name }}
          </div>
          <div class="flex items-center">
            <CalendarIcon class="w-4 h-4 mr-1" />
            {{ formatDate(qna.createdAt) }}
          </div>
          <div v-if="qna.updatedAt !== qna.createdAt" class="flex items-center">
            <ClockIcon class="w-4 h-4 mr-1" />
            수정됨: {{ formatDate(qna.updatedAt) }}
          </div>
        </div>
      </div>

      <!-- 본문 -->
      <div class="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div class="prose prose-lg max-w-none" v-html="qna.content"></div>
      </div>

      <!-- 답변 섹션 -->
      <div class="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div class="flex items-center justify-between mb-6">
          <h3 class="text-lg font-semibold text-gray-900 flex items-center">
            <ChatBubbleLeftRightIcon class="w-5 h-5 mr-2 text-primary-600" />
            답변
          </h3>
          
          <!-- 관리자만 답변 작성/수정 가능하다는 안내 -->
          <div v-if="!authStore.isAdmin" class="text-sm text-gray-500">
            * 관리자만 답변을 작성할 수 있습니다
          </div>
        </div>

        <!-- 답변이 없는 경우 -->
        <div v-if="!hasAnswer" class="text-center py-8">
          <QuestionMarkCircleIcon class="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p class="text-gray-600 mb-4">아직 답변이 없습니다.</p>
          <div v-if="authStore.isAdmin" class="space-y-4">
            <textarea
              v-model="answerContent"
              rows="6"
              class="form-input w-full resize-none"
              placeholder="답변을 작성해주세요..."
            />
            <div class="flex justify-end">
              <button
                @click="submitAnswer"
                :disabled="!answerContent.trim() || isSubmittingAnswer"
                class="btn btn-primary btn-sm"
                :class="{ 'opacity-50 cursor-not-allowed': !answerContent.trim() || isSubmittingAnswer }"
              >
                <span v-if="isSubmittingAnswer" class="flex items-center">
                  <div class="spinner w-4 h-4 mr-2"></div>
                  답변 작성 중...
                </span>
                <span v-else>답변 작성</span>
              </button>
            </div>
          </div>
        </div>

        <!-- 답변이 있는 경우 -->
        <div v-else class="space-y-4">
          <div class="bg-blue-50 rounded-lg p-4">
            <div class="prose prose-sm max-w-none" v-html="answerContent"></div>
            <div v-if="authStore.isAdmin" class="mt-4 pt-4 border-t border-blue-200">
              <div class="flex justify-end space-x-2">
                <button
                  v-if="!isEditingAnswer"
                  @click="startEditAnswer"
                  class="btn btn-outline-primary btn-sm"
                >
                  <PencilIcon class="w-4 h-4 mr-1" />
                  수정
                </button>
                <template v-else>
                  <button
                    @click="cancelEditAnswer"
                    class="btn btn-outline btn-sm"
                  >
                    취소
                  </button>
                  <button
                    @click="updateAnswer"
                    :disabled="isSubmittingAnswer"
                    class="btn btn-primary btn-sm"
                  >
                    <span v-if="isSubmittingAnswer" class="flex items-center">
                      <div class="spinner w-3 h-3 mr-1"></div>
                      수정 중...
                    </span>
                    <span v-else>수정 완료</span>
                  </button>
                </template>
              </div>
            </div>
          </div>

          <!-- 답변 수정 폼 -->
          <div v-if="isEditingAnswer" class="mt-4">
            <textarea
              v-model="editAnswerContent"
              rows="6"
              class="form-input w-full resize-none"
              placeholder="답변을 수정해주세요..."
            />
          </div>
        </div>
      </div>

      <!-- 액션 버튼 -->
      <div class="flex justify-between">
        <RouterLink to="/qna" class="btn btn-outline btn-sm">
          <ArrowLeftIcon class="w-4 h-4 mr-2" />
          목록으로 돌아가기
        </RouterLink>
      </div>
    </div>

    <div v-else class="text-center py-12">
      <ExclamationTriangleIcon class="w-16 h-16 text-gray-300 mx-auto mb-4" />
      <h3 class="text-lg font-medium text-gray-900 mb-2">Q&A를 찾을 수 없습니다</h3>
      <p class="text-gray-600 mb-6">요청하신 Q&A가 존재하지 않거나 삭제되었을 수 있습니다.</p>
      <RouterLink to="/qna" class="btn btn-primary btn-sm">
        Q&A 목록으로
      </RouterLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { RouterLink, useRouter, useRoute } from 'vue-router'
import {
  PencilIcon,
  TrashIcon,
  UserIcon,
  CalendarIcon,
  ClockIcon,
  ArrowLeftIcon,
  ExclamationTriangleIcon,
  ChatBubbleLeftRightIcon,
  QuestionMarkCircleIcon
} from '@heroicons/vue/24/outline'
import { useAuthStore } from '@/stores/auth'
import { useBreadcrumbStore } from '@/stores/breadcrumbs'
import { qnaApi } from '@/services/api'
import type { Qna } from '@/types'
import { useToast } from 'vue-toastification'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const breadcrumbStore = useBreadcrumbStore()
const toast = useToast()

const qna = ref<Qna | null>(null)
const loading = ref(false)
const answerContent = ref('')
const editAnswerContent = ref('')
const isEditingAnswer = ref(false)
const isSubmittingAnswer = ref(false)
const hasAnswer = ref(false)

const qnaId = computed(() => {
  return Number(route.params.id)
})

const canManageQna = computed(() => {
  return authStore.isAdmin || (authStore.user && qna.value && qna.value.author.id === authStore.user.id)
})

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const loadQna = async () => {
  try {
    loading.value = true
    const response = await qnaApi.getQna(qnaId.value)
    qna.value = response.data
    breadcrumbStore.setLabel('QnaDetail', qna.value.title)
    
    // 실제 구현에서는 답변 데이터가 있을 경우 처리
    // 현재는 임시로 답변 없음 상태로 설정
    hasAnswer.value = false
    answerContent.value = ''
  } catch (error: any) {
    console.error('Failed to load qna:', error)
    if (error.response?.status === 404) {
      toast.error('Q&A를 찾을 수 없습니다.')
    } else {
      toast.error('Q&A를 불러오는데 실패했습니다.')
    }
  } finally {
    loading.value = false
  }
}

const deleteQna = async () => {
  if (!qna.value) return
  if (!confirm('정말 삭제하시겠습니까?')) return

  try {
    await qnaApi.deleteQna(qna.value.id)
    toast.success('Q&A가 삭제되었습니다.')
    router.push('/qna')
  } catch (error: any) {
    console.error('Failed to delete qna:', error)
    toast.error('Q&A 삭제에 실패했습니다.')
  }
}

const submitAnswer = async () => {
  if (!answerContent.value.trim()) return

  try {
    isSubmittingAnswer.value = true
    // 실제 구현에서는 답변 API 호출
    hasAnswer.value = true
    toast.success('답변이 작성되었습니다.')
  } catch (error: any) {
    console.error('Failed to submit answer:', error)
    toast.error('답변 작성에 실패했습니다.')
  } finally {
    isSubmittingAnswer.value = false
  }
}

const startEditAnswer = () => {
  editAnswerContent.value = answerContent.value
  isEditingAnswer.value = true
}

const cancelEditAnswer = () => {
  editAnswerContent.value = ''
  isEditingAnswer.value = false
}

const updateAnswer = async () => {
  if (!editAnswerContent.value.trim()) return

  try {
    isSubmittingAnswer.value = true
    // 실제 구현에서는 답변 수정 API 호출
    answerContent.value = editAnswerContent.value
    isEditingAnswer.value = false
    toast.success('답변이 수정되었습니다.')
  } catch (error: any) {
    console.error('Failed to update answer:', error)
    toast.error('답변 수정에 실패했습니다.')
  } finally {
    isSubmittingAnswer.value = false
  }
}

onMounted(() => {
  if (qnaId.value) {
    loadQna()
  }
})
</script>

<style scoped>
.prose {
  color: #374151;
  line-height: 1.75;
}

.prose h1, .prose h2, .prose h3, .prose h4 {
  color: #111827;
  font-weight: 600;
  margin-top: 2rem;
  margin-bottom: 1rem;
}

.prose p {
  margin-bottom: 1.25rem;
}

.prose ul, .prose ol {
  margin-bottom: 1.25rem;
  padding-left: 1.5rem;
}

.prose blockquote {
  border-left: 4px solid #e5e7eb;
  padding-left: 1rem;
  font-style: italic;
  color: #6b7280;
  margin: 1.5rem 0;
}

.prose img {
  border-radius: 0.5rem;
  margin: 1.5rem 0;
}

.prose a {
  color: #3b82f6;
  text-decoration: underline;
}

.prose a:hover {
  color: #1d4ed8;
}

.prose pre {
  background-color: #f3f4f6;
  border-radius: 0.5rem;
  padding: 1rem;
  overflow-x: auto;
  margin: 1.5rem 0;
}

.prose code {
  background-color: #f3f4f6;
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
  font-size: 0.875em;
}
</style>
